import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const HardwareShipping = () => {
    return (
        <BlogLayout
            title="Shipping Your Sovereign AI: Logistics, Customs, and Full Transparency"
            date="Feb 21, 2026"
            readTime={3}
            heroImage="/images/blog/hardware-shipping-hero.png"
            description="We ship high-end, configured physical hardware across international borders. Here are the physical logistics and customs realities we want you to know."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                When you reserve a RoofClaw build slot, you aren't just downloading a piece of software. You are commissioning the physical build, configuration, and secure hardening of a dedicated Apple Silicon device—a Mac Mini or MacBook Air—that will act as your roofing company's autonomous Chief of Staff.
            </p>
            <p>
                Because we are shipping high-end, configured physical hardware across international borders (from our lab in British Columbia, Canada, direct to your office in the United States), there are physical logistics and customs realities we want to be completely transparent about.
            </p>
            <p>
                We do things by the book. No "gray market" shipping. No falsified "friend-to-friend gift" declarations.
            </p>
            <p className="font-bold text-[#7B61FF]">
                Here is exactly what you can expect when your RoofClaw node leaves our facility and heads to your door.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">By-The-Book Commercial Export</h2>
            <p>
                Every RoofClaw system is exported as a formal commercial sale.
            </p>
            <p>
                We utilize top-tier international couriers (like FedEx or UPS Express/Ground) to ensure your hardware is handled with care and tracked every step of the way. Because these are high-value electronics (ranging from $1,000 to $2,000+ per unit before our configuration services), they must undergo formal U.S. Customs and Border Protection (CBP) clearance.
            </p>
            <p>
                We declare exactly what is in the box: a custom-configured Apple Mac Mini or MacBook Air (HS code 8471.30).
            </p>
            <p>
                We do this because <strong className="text-[#F0EFF4]">your business continuity matters</strong>. Attempting to skirt customs by declaring a $1,500 computer as a "gift" is a fast track to having the equipment seized or permanently delayed at the border. We do not take risks with your operational hardware.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">The Good News: Zero Duties on the Hardware</h2>
            <p>
                Thanks to current trade agreements (USMCA), portable computers shipped from Canada into the United States enter <strong className="text-[#10B981]">duty-free (0%)</strong>.
            </p>
            <p>
                You will not be hit with a massive tariff simply because the computer was configured in our Canadian lab.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">The Reality: Small Customs & Brokerage Fees</h2>
            <p>
                While the hardware itself is duty-free, U.S. Customs and the courier will still assess nominal processing fees for bringing a commercial electronic device into the country.
            </p>
            <p className="font-bold mb-4">What you will likely pay at the door:</p>
            <ul className="list-disc pl-6 mb-8 text-[#F0EFF4]/80 space-y-4">
                <li><strong>Merchandise Processing Fee (MPF):</strong> A marginal fee assessed by U.S. Customs (typically under $5).</li>
                <li><strong>Courier Brokerage / Advancement Fees:</strong> FedEx and UPS charge a fee to act as your customs broker and push the paperwork through on your behalf.
                    <ul className="list-circle pl-6 mt-2 space-y-1">
                        <li>If we ship via <strong>Ground</strong>, this is typically $35–$50 USD.</li>
                        <li>If we ship via <strong>Express</strong>, this is often much lower (around $10–$20 USD) as clearance is generally included in the premium transit cost.</li>
                    </ul>
                </li>
            </ul>
            <p>
                To keep the process as streamlined as possible on our end, we ship all hardware <strong className="text-[#F0EFF4]">DDU (Delivered Duty Unpaid)</strong>.
            </p>
            <p>
                This simply means that when your RoofClaw node arrives, FedEx or UPS will send you an invoice for the small brokerage fees (roughly $20–$60 USD total) to release the package.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">State-Level Sales Tax</h2>
            <p>
                As a business operating out of British Columbia, we do not have an economic nexus to collect local state sales taxes for 1-10 unit shipments. Therefore, you will not see U.S. state sales tax on your RoofClaw invoice.
            </p>
            <p>
                However, depending on your state's laws (e.g., Pennsylvania, Texas, etc.), your local government or the courier may assess a state use-tax upon import.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">Summary: No Surprises.</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    We price the RoofClaw system to include the hardware, our extensive pre-configuration labor, the secure networking setup, and the 1-on-1 onboarding consults.
                </p>
                <p className="text-xl font-drama italic text-[#7B61FF]">
                    When your node arrives, you will be responsible for a nominal courier brokerage fee (usually $20–$60 USD) to clear Customs—a small price to pay for doing things legally, safely, and ensuring your autonomous AI brain arrives at your office intact and ready to work.
                </p>
            </div>
        </BlogLayout>
    );
};

export default HardwareShipping;
